import React from 'react';

const Timeline = ({ statusHistory }) => {
  if (!statusHistory || statusHistory.length === 0) {
    return (
      <div className="text-center text-gray-500 py-4">
        <p>No status changes recorded yet.</p>
      </div>
    );
  }

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-blue-100 text-blue-800';
      case 'under_investigation':
        return 'bg-yellow-100 text-yellow-800';
      case 'resolved':
        return 'bg-green-100 text-green-800';
      case 'dismissed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending':
        return 'Pending';
      case 'under_investigation':
        return 'Under Investigation';
      case 'resolved':
        return 'Resolved';
      case 'dismissed':
        return 'Dismissed';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
          </svg>
        );
      case 'under_investigation':
        return (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'resolved':
        return (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        );
      case 'dismissed':
        return (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        );
    }
  };

  return (
    <div className="space-y-4">
      <h4 className="font-semibold text-gray-900 mb-4">Status History</h4>
      <div className="flow-root">
        <ul className="-mb-8">
          {statusHistory.map((entry, index) => (
            <li key={entry.id || index}>
              <div className="relative pb-8">
                {index !== statusHistory.length - 1 && (
                  <span
                    className="absolute top-5 left-5 -ml-px h-full w-0.5 bg-gray-200"
                    aria-hidden="true"
                  />
                )}
                <div className="relative flex items-start space-x-3">
                  <div className="relative">
                    <div className={`
                      h-10 w-10 rounded-full flex items-center justify-center ring-8 ring-white
                      ${getStatusBadgeColor(entry.new_status)}
                    `}>
                      {getStatusIcon(entry.new_status)}
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm">
                      <div className="flex items-center space-x-2">
                        <span className="font-medium text-gray-900">
                          Status changed to
                        </span>
                        <span className={`
                          inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                          ${getStatusBadgeColor(entry.new_status)}
                        `}>
                          {getStatusLabel(entry.new_status)}
                        </span>
                      </div>
                      {entry.old_status && (
                        <div className="mt-1 text-gray-500">
                          Previous status:
                          <span className={`
                            ml-1 inline-flex items-center px-2 py-0.5 rounded text-xs
                            ${getStatusBadgeColor(entry.old_status)}
                          `}>
                            {getStatusLabel(entry.old_status)}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="mt-2 text-sm text-gray-700">
                      <p className="text-gray-500">
                        {new Date(entry.created_at).toLocaleString()}
                        {entry.changed_by && (
                          <span className="ml-2">
                            by {entry.changed_by}
                          </span>
                        )}
                      </p>
                      {entry.notes && (
                        <div className="mt-2 p-3 bg-gray-50 rounded-md">
                          <p className="text-sm text-gray-700 font-medium mb-1">Notes:</p>
                          <p className="text-sm text-gray-600">{entry.notes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default Timeline;
